/**
 * Network error pattern matching. Extracted into a dependency-free leaf module
 * so both the global handler (network-error-handler.ts) and the monitoring noise
 * filter (monitoring.ts) can share it without forming an import cycle
 * (issue #1531).
 */

/**
 * Network error message/name patterns. Single source of truth shared with the
 * monitoring noise filter in monitoring.ts (shouldReportError).
 */
export const NETWORK_ERROR_PATTERNS = [
  'failed to fetch',
  'network error',
  'networkerror',
  'fetch error',
  'load failed',
  'the network connection was lost',
  'the internet connection appears to be offline',
  'aborted',
  'abort error',
  'request aborted',
  'canceled',
];

/**
 * Match a single error object against the network patterns.
 * The generated API client wraps the real cause in a FetchError whose own
 * name is "FetchError" but whose message is generic ("The request failed
 * and the interceptors did not return an alternative response"), so the
 * explicit name check is required - pattern matching alone would miss it.
 */
function matchesNetworkPattern(error: any): boolean {
  if (!error) return false;
  if (error?.name === 'FetchError') return true;

  const errorMessage = error?.message?.toLowerCase() || '';
  const errorName = error?.name?.toLowerCase() || '';

  return NETWORK_ERROR_PATTERNS.some((pattern) => errorMessage.includes(pattern) || errorName.includes(pattern));
}

/**
 * Check if an error is a network error that should be ignored by monitoring.
 * Inspects both the error itself and its `cause` - the API client's
 * FetchError carries the native "Failed to fetch" / "Load failed" on .cause.
 */
export function isIgnorableNetworkError(error: any): boolean {
  if (!error) return false;
  return matchesNetworkPattern(error) || matchesNetworkPattern(error?.cause);
}
